import {
  eq,
  desc,
  asc,
  ilike,
  or,
  and,
  count as drizzleCount,
  getTableName,
  getTableColumns,
  type Schema,
  type SQL,
} from "drizzle-orm";
import type { PgTable, AnyPgColumn } from "drizzle-orm/pg-core";
import type { GencowCtx, GencowDefaultUserIdentity } from "./context.js";
import { buildQuerySubscriptionKey } from "./reactive-realtime.js";
import type { GencowProcedureBuilder } from "./procedure.js";
import { v } from "./v.js";
import { assertStructuredFiltersAllowlist, parseFilterNode } from "./createCrud.filters.js";
import type { CrudOptions, CrudTableLike, GencowCrudDefinition } from "./createCrud.types.js";
import { detectOwnerMeta } from "./createCrud.owner.js";
import {
  buildCrudCreateInputValidator,
  buildCrudUpdateInputValidator,
  buildRowOutputValidator,
  buildSelectProjection,
  detectIdType,
} from "./createCrud.validators.js";
import {
  buildTupleComparePredicate,
  decodeCursor,
  encodeCursor,
  normalizeOrderDir,
} from "./createCrud.cursor.js";

type CrudInternalMeta = NonNullable<GencowCrudDefinition["meta"]>;

async function denyUnless<TCtx>(
  policy: ((ctx: TCtx) => boolean | Promise<boolean>) | undefined,
  ctx: TCtx,
) {
  if (!policy) return;
  const ok = await policy(ctx);
  if (!ok) throw new Error("Forbidden");
}

function buildCrud<T extends CrudTableLike, TSchema extends Schema, TUser extends GencowDefaultUserIdentity>(
  table: T,
  options: CrudOptions<T, GencowCtx<TSchema, TUser>> | undefined,
  procedure: {
    query: GencowProcedureBuilder<"query", GencowCtx<TSchema, TUser>>;
    mutation: GencowProcedureBuilder<"mutation", GencowCtx<TSchema, TUser>>;
  },
): GencowCrudDefinition<GencowCtx<TSchema, TUser>> {
  const { query, mutation } = procedure;
  const anyTable = table as any;
  const drizzleTable = table as unknown as PgTable;
  const tableName: string = getTableName(drizzleTable);
  const prefix = options?.prefix || tableName;
  const allowAnonymous = options?.allowAnonymous ?? false;
  const useRealtime = options?.realtime ?? true;
  const defaultLimit = options?.defaultLimit ?? 20;
  const maxLimit = options?.maxLimit ?? 100;
  const pk = anyTable["id"] as AnyPgColumn;

  if (!pk) {
    throw new Error(`[createCrud] Table "${tableName}" must have an 'id' column.`);
  }

  const pkPropName = Object.keys(getTableColumns(drizzleTable)).find((k) => anyTable[k] === pk) ?? "id";

  const idValidator = detectIdType(pk);
  const createdAtCol = anyTable["createdAt"] as AnyPgColumn | undefined;
  const defaultOrderCol = createdAtCol || pk;
  const userIdCol = anyTable["userId"] as AnyPgColumn | undefined;
  const ownerMeta = detectOwnerMeta(drizzleTable);
  const disableILike = options?.disableILike === true;

  const cursorPropNames = options?.cursor ? [...options.cursor].map((c) => String(c)) : undefined;

  let sortablePropNames: Set<string> | undefined =
    options?.sortable && options.sortable.length > 0
      ? new Set(options.sortable.map((c) => String(c)))
      : undefined;

  if (cursorPropNames?.length) {
    sortablePropNames ??= new Set<string>();
    for (const c of cursorPropNames) {
      sortablePropNames.add(c);
      if (!(c in anyTable)) {
        throw new Error(`[createCrud] cursor column "${c}" is not present on table "${tableName}"`);
      }
    }
    const last = cursorPropNames[cursorPropNames.length - 1];
    if (last !== pkPropName) {
      console.warn(
        `[createCrud] "${tableName}": composite cursor ends with "${last}" — include primary key (${pkPropName}) as final segment for stable pagination.`,
      );
    }
  }

  const drizzleCols = getTableColumns(drizzleTable) as Record<string, AnyPgColumn>;

  async function assertReadAccess(ctx: GencowCtx<TSchema, TUser>) {
    await denyUnless(options?.access?.read, ctx);
  }

  function buildWhereConditions(args: {
    search?: string;
    filters?: Record<string, unknown>;
  }): SQL | undefined {
    const conditions: SQL[] = [];

    if (options?.softDelete) {
      const sdField = anyTable[options.softDelete.field as string] as AnyPgColumn;
      conditions.push(eq(sdField, null as any));
    }

    if (args.search && options?.searchFields?.length) {
      if (disableILike) {
        throw new Error(`Search is disabled (disableILike) for CRUD "${prefix}"`);
      }
      const searchConds = options.searchFields.map((f) =>
        ilike(anyTable[f as string] as AnyPgColumn, `%${args.search}%`),
      );
      conditions.push(or(...searchConds)!);
    }

    if (args.filters && typeof args.filters === "object" && args.filters !== null) {
      const filterObj = args.filters as Record<string, unknown>;
      const hasShape = Object.keys(filterObj).length > 0;
      if (hasShape) {
        assertStructuredFiltersAllowlist(filterObj, options?.allowedFilters as string[] | undefined);
        const allowedFields =
          options?.allowedFilters !== undefined ? (options.allowedFilters as string[]) : undefined;
        const filterCondition = parseFilterNode(filterObj, anyTable, allowedFields, 0, {
          disableILike,
          strictColumnOps: true,
        });
        if (filterCondition) conditions.push(filterCondition);
      }
    }

    return conditions.length > 0 ? and(...conditions) : undefined;
  }

  async function inRlsOrPlainTx<TTx>(db: any, fn: (tx: any) => Promise<TTx>): Promise<TTx> {
    if (typeof db?.transaction === "function") {
      return await db.transaction(fn);
    }
    return await fn(db);
  }

  function invalidateListRealtime(ctx: GencowCtx<TSchema, TUser>) {
    ctx.realtime.invalidate(`${prefix}.list`);
  }

  function invalidateGetRealtime(ctx: GencowCtx<TSchema, TUser>, id: unknown) {
    ctx.realtime.invalidate(buildQuerySubscriptionKey(`${prefix}.get`, { id }));
  }

  const pickCols = options?.columns?.pick as readonly string[] | undefined;
  const omitCols = options?.columns?.omit as readonly string[] | undefined;
  const listProjection = buildSelectProjection(drizzleCols, pickCols as any, omitCols as any);
  const getProjection = listProjection;
  const projectedColumns =
    listProjection !== undefined ? (listProjection as Record<string, AnyPgColumn>) : drizzleCols;
  const listRowOutput = buildRowOutputValidator(projectedColumns);
  /**
   * Same row shape as list/get/create success responses.
   * Update legitimately returns `null` when zero rows matched (missing id, RLS, or owner-scoped where).
   */
  const updateOutputSchema = v.nullable(listRowOutput);
  const getOutput = v.nullable(listRowOutput);
  const createInputValidator = buildCrudCreateInputValidator(drizzleCols, {
    serverInjectedFields: ownerMeta ? [ownerMeta.propertyName] : undefined,
  });
  const updateInputValidator = buildCrudUpdateInputValidator(drizzleCols, pkPropName, idValidator);
  const listOutput = v.object({
    data: v.array(listRowOutput),
    total: v.number(),
    nextCursor: v.optional(v.string()),
  });

  const enabledMethods = new Set(options?.methods ?? ["list", "get", "create", "update", "remove"]);
  const enabledMethodsList = Array.from(enabledMethods) as CrudInternalMeta["methods"];

  const internalMeta: CrudInternalMeta = {
    tableName,
    prefix,
    methods: enabledMethodsList,
    allowedFilters: (options?.allowedFilters ?? []).map((field) => String(field)),
    searchFields: (options?.searchFields ?? []).map((field) => String(field)),
    sortable: sortablePropNames ? [...sortablePropNames] : undefined,
    cursor: cursorPropNames,
    ...(disableILike ? { disableILike: true } : {}),
    allowAnonymous,
    realtime: useRealtime,
  };

  /** sortable 미지정이면 존재하는 테이블 컬럼으로 정렬 허용. 지정하면 orderBy 화이트리스트 검사. */
  function assertOrderBy(orderByProp: string | undefined) {
    if (!orderByProp) return;
    if (!(orderByProp in anyTable)) throw new Error(`Unknown orderBy field "${orderByProp}"`);
    if (sortablePropNames && !sortablePropNames.has(orderByProp)) {
      throw new Error(`orderBy field "${orderByProp}" is not sortable`);
    }
  }

  const listDef = !enabledMethods.has("list")
    ? undefined
    : (allowAnonymous ? query.allowAnonymous() : query)
        .name(`${prefix}.list`)
        .input(
          v.object({
            page: v.optional(v.number()),
            limit: v.optional(v.number()),
            cursor: v.optional(v.string()),
            search: v.optional(v.string()),
            orderBy: v.optional(v.string()),
            orderDir: v.optional(v.string()),
            filters: v.optional(v.any()),
          }),
        )
        .output(listOutput)
        .handler(async ({ context, input }) => {
          await assertReadAccess(context);
          const needsAuth = !allowAnonymous || (ownerMeta && !ownerMeta.readPublic);
          const user = needsAuth ? context.auth.requireAuth() : null;
          let whereClause = buildWhereConditions(input);

          if (ownerMeta && !ownerMeta.readPublic && user) {
            const ownerFilter = eq(ownerMeta.column, user.id);
            whereClause = whereClause ? and(whereClause, ownerFilter) : ownerFilter;
          }

          const limit = Math.min(Math.max(1, input.limit ?? defaultLimit), maxLimit);
          let orderExpressions: SQL[] = [];
          const orderDirEff = normalizeOrderDir(input.orderDir);

          const cursorCols =
            cursorPropNames?.map((name) => anyTable[name] as AnyPgColumn).filter(Boolean) ?? [];

          if (cursorPropNames?.length) {
            if (input.page != null && input.page !== undefined) {
              throw new Error(
                'Offset "page" is not supported when cursor columns are configured; use cursor',
              );
            }
            if (input.orderBy != null && input.orderBy !== undefined) {
              throw new Error("orderBy is fixed when composite cursor columns are configured; omit orderBy");
            }
            for (const c of cursorCols) {
              orderExpressions.push(orderDirEff === "asc" ? asc(c) : desc(c));
            }
          } else {
            const ob = input.orderBy;
            assertOrderBy(ob);
            if (ob && anyTable[ob]) {
              const col = anyTable[ob] as AnyPgColumn;
              orderExpressions = [orderDirEff === "asc" ? asc(col) : desc(col)];
            } else {
              orderExpressions = [desc(defaultOrderCol)];
            }
          }

          let cursorSeek: SQL | undefined;
          if (cursorCols.length && input.cursor) {
            const decoded = decodeCursor(input.cursor);
            if (decoded.direction !== orderDirEff) {
              throw new Error("cursor was issued for a different sort direction");
            }
            if (decoded.values.length !== cursorCols.length) {
              throw new Error("cursor value count does not match configured cursor columns");
            }
            cursorSeek = buildTupleComparePredicate(cursorCols, decoded.values, orderDirEff);
            whereClause = whereClause ? and(whereClause, cursorSeek)! : cursorSeek;
          }

          return await inRlsOrPlainTx(context.db, async (tx: any) => {
            const offset = cursorPropNames?.length
              ? 0
              : Math.max(0, (Math.max(1, input.page ?? 1) - 1) * limit);
            const base =
              listProjection !== undefined
                ? tx.select(listProjection).from(anyTable)
                : tx.select().from(anyTable);
            const results = await base
              .where(whereClause)
              .orderBy(...(orderExpressions as [SQL, ...SQL[]]))
              .limit(limit)
              .offset(offset);

            const countResult = await tx.select({ count: drizzleCount() }).from(anyTable).where(whereClause);
            const total = Number(countResult[0]?.count ?? 0);

            let nextCursor: string | undefined;
            if (cursorPropNames?.length && results.length === limit) {
              const last = results[results.length - 1] as Record<string, unknown>;
              const vals = cursorPropNames.map((prop) => last[prop]);
              nextCursor = encodeCursor(vals as unknown[], orderDirEff);
            }

            return { data: results, total, nextCursor };
          });
        });

  const getDef = !enabledMethods.has("get")
    ? undefined
    : (allowAnonymous ? query.allowAnonymous() : query)
        .name(`${prefix}.get`)
        .input(v.object({ id: idValidator }))
        .output(getOutput)
        .handler(async ({ context, input }) => {
          await assertReadAccess(context);
          const needsAuth = !allowAnonymous || (ownerMeta && !ownerMeta.readPublic);
          const user = needsAuth ? context.auth.requireAuth() : null;

          let whereCond: SQL = eq(pk, input.id);
          if (ownerMeta && !ownerMeta.readPublic && user) {
            whereCond = and(whereCond, eq(ownerMeta.column, user.id))!;
          }

          if (options?.softDelete) {
            const sdField = anyTable[options.softDelete.field as string] as AnyPgColumn;
            whereCond = and(whereCond, eq(sdField, null as any))!;
          }

          return await inRlsOrPlainTx(context.db, async (tx: any) => {
            const qb =
              getProjection !== undefined
                ? tx.select(getProjection).from(anyTable)
                : tx.select().from(anyTable);
            const [result] = await qb.where(whereCond).limit(1);
            return result ?? null;
          });
        });

  const createDef = !enabledMethods.has("create")
    ? undefined
    : (allowAnonymous ? mutation.allowAnonymous() : mutation)
        .name(`${prefix}.create`)
        .input(createInputValidator)
        .output(listRowOutput)
        .handler(async ({ context, input }) => {
          await denyUnless(options?.access?.create, context);
          const user = ownerMeta || !allowAnonymous ? context.auth.requireAuth() : null;
          let insertData = typeof input === "object" && input !== null ? { ...input } : {};

          if (ownerMeta && user) {
            const requestedOwner =
              insertData[ownerMeta.propertyName] ?? insertData[ownerMeta.columnName] ?? insertData.userId;
            if (requestedOwner != null && requestedOwner !== user.id) {
              throw new Error("Forbidden: cannot create resource for another user");
            }
          }

          if (ownerMeta && user) {
            insertData[ownerMeta.propertyName] = user.id;
          } else if (userIdCol && user && !insertData.userId) {
            insertData.userId = user.id;
          }

          if (options?.hooks?.beforeCreate) {
            insertData = await options.hooks.beforeCreate(insertData);
          }

          const [result] = await inRlsOrPlainTx(context.db, async (tx: any) =>
            listProjection !== undefined
              ? tx.insert(anyTable).values(insertData).returning(listProjection)
              : tx.insert(anyTable).values(insertData).returning(),
          );

          if (useRealtime && enabledMethods.has("list")) {
            invalidateListRealtime(context);
          }

          return result;
        });

  const updateDef = !enabledMethods.has("update")
    ? undefined
    : (allowAnonymous ? mutation.allowAnonymous() : mutation)
        .name(`${prefix}.update`)
        .input(updateInputValidator)
        .output(updateOutputSchema)
        .handler(async ({ context, input }) => {
          await denyUnless(options?.access?.update, context);
          const user = ownerMeta || !allowAnonymous ? context.auth.requireAuth() : null;
          const args = typeof input === "object" && input !== null ? (input as Record<string, unknown>) : {};
          const idVal = args.id;
          let updateData = { ...args };
          delete updateData.id;

          let updateWhere: SQL = eq(pk, idVal);
          if (ownerMeta && user) {
            updateWhere = and(eq(pk, idVal), eq(ownerMeta.column, user.id))!;
            delete updateData[ownerMeta.propertyName];
            if (ownerMeta.propertyName !== ownerMeta.columnName) {
              delete updateData[ownerMeta.columnName];
            }
          }

          if (anyTable["updatedAt"]) {
            (updateData as any).updatedAt = new Date();
          }

          if (options?.hooks?.beforeUpdate) {
            updateData = await options.hooks.beforeUpdate(updateData);
          }

          const [result] = await inRlsOrPlainTx(context.db, async (tx: any) =>
            listProjection !== undefined
              ? tx.update(anyTable).set(updateData).where(updateWhere).returning(listProjection)
              : tx.update(anyTable).set(updateData).where(updateWhere).returning(),
          );

          if (useRealtime) {
            if (enabledMethods.has("list")) {
              invalidateListRealtime(context);
            }
            if (enabledMethods.has("get")) {
              const getKey = buildQuerySubscriptionKey(`${prefix}.get`, { id: idVal });
              if (allowAnonymous && !ownerMeta) context.realtime.emit(getKey, result);
              else invalidateGetRealtime(context, idVal);
            }
          }

          return result ?? null;
        });

  const removeDef = !enabledMethods.has("remove")
    ? undefined
    : (allowAnonymous ? mutation.allowAnonymous() : mutation)
        .name(`${prefix}.remove`)
        .input(v.object({ id: idValidator }))
        .output(v.object({ success: v.boolean() }))
        .handler(async ({ context, input }) => {
          await denyUnless(options?.access?.remove, context);
          const user = ownerMeta || !allowAnonymous ? context.auth.requireAuth() : null;

          let deleteWhere: SQL = eq(pk, input.id);
          if (ownerMeta && user) {
            deleteWhere = and(eq(pk, input.id), eq(ownerMeta.column, user.id))!;
          }

          await inRlsOrPlainTx(context.db, async (tx: any) => {
            if (options?.softDelete) {
              const sdField = options.softDelete.field as string;
              await tx
                .update(anyTable)
                .set({ [sdField]: new Date() } as any)
                .where(deleteWhere);
            } else {
              await tx.delete(anyTable).where(deleteWhere);
            }
          });

          if (useRealtime && enabledMethods.has("list")) {
            invalidateListRealtime(context);
          }

          return { success: true };
        });

  return {
    list: listDef,
    get: getDef,
    create: createDef,
    update: updateDef,
    remove: removeDef,
    meta: internalMeta,
    prefix,
  };
}

/**
 * Returns a `createCrud` function bound to `procedure.query` / `procedure.mutation` from
 * {@link createGencowProcedureBuilders} — re-export the result from `gencow/runtime.ts`.
 */
export function createCrudFactory<
  TSchema extends Schema,
  TUser extends GencowDefaultUserIdentity = GencowDefaultUserIdentity,
>(procedure: {
  query: GencowProcedureBuilder<"query", GencowCtx<TSchema, TUser>>;
  mutation: GencowProcedureBuilder<"mutation", GencowCtx<TSchema, TUser>>;
}) {
  return function createCrud<T extends CrudTableLike>(
    table: T,
    options?: CrudOptions<T, GencowCtx<TSchema, TUser>>,
  ): GencowCrudDefinition<GencowCtx<TSchema, TUser>> {
    return buildCrud(table, options, procedure);
  };
}
